import { Card, CardDescription, CardTitle, Skeleton } from '@newfront-insurance/core-ui/v2';
import { Loader2 } from 'lucide-react';
import { useId, type ReactNode } from 'react';

/** Shared presentation for titled lists of settings items; each feature owns its data and actions. */
export function SettingsSectionList({
  title,
  headerEnd,
  loading = false,
  children,
  emptyMessage,
}: {
  title: string;
  headerEnd?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
  emptyMessage?: string;
}) {
  const titleId = useId();
  return (
    <Card className="border-steel-75 overflow-hidden bg-white font-sans shadow-none">
      <div className="border-steel-75 bg-steel-50 flex min-h-9 flex-wrap items-center justify-between gap-3 border-b px-3.5 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <h3 id={titleId} className="text-steel-500 text-sm leading-4 font-semibold">
            {title}
          </h3>
          {loading ? <Loader2 aria-hidden="true" className="text-steel-400 size-4 animate-spin" /> : null}
        </div>
        {headerEnd}
      </div>
      <div aria-busy={loading}>
        {emptyMessage ? <p className="text-steel-400 px-3.5 py-4 text-base leading-5">{emptyMessage}</p> : null}
        {children ? <ul aria-labelledby={titleId}>{children}</ul> : null}
      </div>
    </Card>
  );
}

export function SettingsSectionListRow({
  title,
  description,
  leading,
  trailing,
  loadingLabel,
}: {
  title?: string;
  description?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  loadingLabel?: string;
}) {
  return (
    <li className="border-steel-75 even:bg-steel-50 flex min-h-13 flex-wrap items-center gap-3 border-b px-3.5 py-2 last:border-b-0">
      <div className="flex min-w-0 flex-1 basis-40 items-center gap-3">
        {leading ? <div className="shrink-0">{leading}</div> : null}
        {loadingLabel ? (
          <div role="status" aria-label={loadingLabel} className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="bg-steel-100 h-4 w-32 max-w-full" />
            <Skeleton className="bg-steel-100 h-3 w-44 max-w-full" />
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <CardTitle className="text-steel-500 truncate leading-5" title={title}>
              {title}
            </CardTitle>
            {description ? (
              <CardDescription className="text-steel-400 truncate text-sm leading-4" title={description}>
                {description}
              </CardDescription>
            ) : null}
          </div>
        )}
      </div>
      {trailing ? <div className="ml-auto max-w-full shrink-0">{trailing}</div> : null}
    </li>
  );
}
